import type { ReactNode } from 'react'
import { useId } from 'react'
import styles from './Form.module.css'

/**
 * The controls used on the reading surfaces: profile, settings, diagnostics.
 *
 * Drawn rather than borrowed from the platform, so a switch here belongs to
 * the same hairline family as the Blossom and the icons. All of them meet the
 * touch-target floor and expose real ARIA state rather than relying on colour.
 */

export function Row({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children?: ReactNode
}) {
  return (
    <div className={styles.row}>
      <span className={styles.rowText}>
        <span className={styles.rowLabel}>{label}</span>
        {hint ? <span className={styles.rowHint}>{hint}</span> : null}
      </span>
      {children}
    </div>
  )
}

export function Switch({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  onChange(next: boolean): void
}) {
  const id = useId()
  return (
    <div className={styles.row}>
      <span className={styles.rowText}>
        <span className={styles.rowLabel} id={id}>
          {label}
        </span>
        {hint ? <span className={styles.rowHint}>{hint}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={id}
        className={styles.switch}
        onClick={() => onChange(!checked)}
      >
        <span className={styles.knob} />
      </button>
    </div>
  )
}

export interface Choice<T extends string> {
  value: T
  label: string
  /** Small trailing note, e.g. marking a language as unreviewed. */
  note?: string
}

export function ChoiceGroup<T extends string>({
  label,
  hint,
  value,
  choices,
  onChange,
}: {
  label: string
  hint?: string
  value: T
  choices: ReadonlyArray<Choice<T>>
  onChange(next: T): void
}) {
  return (
    <>
      <Row label={label} hint={hint} />
      <div className={styles.choices} role="radiogroup" aria-label={label}>
        {choices.map((choice) => (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={choice.value === value}
            className={styles.choice}
            onClick={() => onChange(choice.value)}
          >
            {choice.label}
            {choice.note ? ` (${choice.note})` : ''}
          </button>
        ))}
      </div>
    </>
  )
}


export function ActionButton({
  children,
  onClick,
  variant = 'default',
  testId,
}: {
  children: ReactNode
  onClick(): void
  variant?: 'default' | 'primary' | 'danger'
  testId?: string
}) {
  const className = [
    styles.button,
    variant === 'primary' ? styles.buttonPrimary : '',
    variant === 'danger' ? styles.buttonDanger : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button type="button" className={className} onClick={onClick} data-testid={testId}>
      {children}
    </button>
  )
}

export function ButtonRow({ children }: { children: ReactNode }) {
  return <div className={styles.buttonRow}>{children}</div>
}


export function Stats({ children }: { children: ReactNode }) {
  return <div className={styles.stats}>{children}</div>
}

export function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  )
}
